import { faCirclePlus } from "@fortawesome/pro-regular-svg-icons";
import { faCheck } from "@fortawesome/pro-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { ModalContext } from "@notanumber/nan-component-library";
import Block from "components/General/Block";
import ButtonGsfurn from "components/General/ButtonGsfurn/ButtonGsfurn";
import InputFieldGrinwis from "components/General/InputFieldGrinwis/InputFieldGrinwis";
import NumberInputField from "components/General/NumberInputField/NumberInputField";
import { ModalCustomizationWithoutPadding, ModalCustomizationWithoutPaddingSmall } from "constants/modal";
import { CartContext } from "context/CartContextProvider";
import { SettingsContext } from "context/SettingsContextProvider";
import { usePathname } from "next/navigation";
import { useContext, useEffect, useMemo, useState } from "react";
import ProductPreparePackage from "../ProductPreparePackage/ProductPreparePackage";
import PropertyOption from "./PropertyOption";
import { Badge } from "flowbite-react";
import { UserContext } from "context/UserContextProvider";
import { toEuroFormat } from "hooks/utils/formatter";

const ProductProperties = ({
  preparation_packages,
  selected_image,
  page,
  setSelectedVariationId,
}) => {
  const pathName = usePathname();

  const { settings } = useContext(SettingsContext);
  const { addToCart, updateCartItemPreparation } = useContext(CartContext);
  const { user } = useContext(UserContext);
  const { setOverlay } = useContext(ModalContext);
  const [selectedVariations, setSelectedVariations] = useState({});
  const [quantity, setQuantity] = useState(1);
  const [clearForm, setClearForm] = useState(null);
  const product = page?.product;
  const overlay_settings = settings?.options?.voorbereidingspakket_overlay;
  const upsell = page?.up_sells;
  const { contact: user_contact, prices, variation_price_ranges } = user || {};
  const showPrice = user_contact?.is_dealer;

  const {
    variations,
    is_customizable,
    price_list,
    wc_product_type,
    category_ids_hierarchical,
  } = product || {};

  const is_variable_product = product?.variations?.values && Object.keys(product?.variations?.values).length > 0;

  const {
    translations: translations_list,
    values: variations_list,
    info: variation_info,
    order,
  } = variations || {};

  useEffect(() => {
    setSelectedVariations({});
  }, [pathName]);

  const extra_options = Object.entries(product?.all_properties || {})
    .filter(([, list]) => list.length == 1)
    .map(([slug, list]) => {
      return { slug, value: list[0] };
    })
    .reduce((total, item) => {
      const { slug, value } = item;
      return { ...total, ["attribute_" + slug]: { value, single: true } };
    }, {});

  /**
   * Get all the preparation packages that are possible for this product
   */
  const preparation_package_for_product = useMemo(() => {
    const possible_packages = preparation_packages
      ?.map((prep_package) => {
        const depth = (prep_package?.categories || [])
          ?.map((category) => {
            const cat_id = category?.term_id;

            const product_categories_contains_cat = (
              category_ids_hierarchical || []
            )?.filter((cat) => cat.includes(cat_id));

            if (product_categories_contains_cat.length == 0) {
              return null;
            }

            let depth = 0;
            for (const cat of product_categories_contains_cat) {
              const index = cat.indexOf(cat_id);
              const _depth = cat.length - index;
              if (_depth > depth) {
                depth = _depth;
              }
            }
            return depth;
          })
          .filter((n) => n !== null);

        const max_depth = depth.length > 0 ? Math.max(...depth) : null;
        return { prep_package, max_depth };
      })
      .filter(({ max_depth }) => max_depth !== null);

    const _packages = (possible_packages || [])?.map(({ prep_package, max_depth }) => ({
      ...prep_package,
      max_depth,
    }));

    return _packages;
  }, [preparation_packages]);

  /**
   * Combine ranges to make it easier to check if a given value is within the range
   */
  function simplifyRanges(ranges) {
    const simplified_ranges = ranges.reduce((total, item) => {
      const { minimale_maat, maximale_maat } = item;
      const range = [parseInt(minimale_maat), parseInt(maximale_maat)];
      const last_range = total[total.length - 1];
      if (last_range && last_range[1] + 1 == range[0]) {
        last_range[1] = range[1];
      } else {
        total.push(range);
      }
      return total;
    }, []);

    return simplified_ranges;
  }

  /**
   * Get all the possible price lists for this product
   */
  const price_lists = useMemo(() => {
    if (!price_list) {
      return [];
    }
    const data = [];
    for (const list of price_list) {
      const match_on = list?.match_on?.match_on_id;
      if (!match_on) {
        continue;
      }
      const { heights, widths } = list?.acf || {};
      const width_ranges = simplifyRanges(widths);
      const height_ranges = simplifyRanges(heights);
      data.push({ match_on, width_ranges, height_ranges });
    }
    return data;
  }, [price_list]);

  /**
   * Get the active price list for this product, always the first one matching
   */
  const combined_options = {
    ...(extra_options || {}),
    ...(selectedVariations || {}),
  };

  const _options = Object.values(combined_options);

  const active_price_list = price_lists.find(({ match_on }) =>
    _options.some(
      (variation) =>
        variation?.value?.term_id == match_on ||
        (variation?.value == match_on && variation?.single)
    )
  );

  /**
   * Get the error messages for the height and width fields
   */
  const { hoogte_error, breedte_error } = useMemo(() => {
    let return_value = {};

    const height_field =
      selectedVariations[product?.customize_fields?.hoogte_attribuut];

    const width_field =
      selectedVariations[product?.customize_fields?.breedte_attribuut];

    const fields = [
      {
        field: height_field,
        name: "hoogte",
        size_range: active_price_list?.height_ranges,
      },
      {
        field: width_field,
        name: "breedte",
        size_range: active_price_list?.width_ranges,
      },
    ];

    //Check if the height and width are within the range
    for (const { field, name, size_range } of fields) {
      if (!field || !field?.custom) {
        continue;
      }
      const value = field?.value;
      const fits_within = validateRange(value, size_range);
      if (fits_within) {
        continue;
      }
      return_value[`${name}_error`] = `De ingevulde ${name} moet ${size_range
        .map(([min, max]) => `tussen ${min} en ${max}`)
        .join(" of ")} liggen`;
    }
    return return_value;
  }, [active_price_list, selectedVariations]);

  /**
   * Combine all the translations into one object
   */
  const translations = useMemo(
    () =>
      translations_list?.reduce((total, item) => {
        const trans = Object.entries(item);
        for (const [key, value] of trans) {
          total[key] = value;
        }
        return total;
      }, {}),
    [translations_list]
  );

  /**
   * Check if the product has any custom variations
   */
  const isCustomConfiguration = Object.values(selectedVariations).some(
    (variation) => variation?.custom
  );

  /**
   * Check if the form is submittable
   */
  const isSubmittable =
    (order?.every((slug) => selectedVariations[slug]) &&
      !hoogte_error &&
      !breedte_error &&
      isSomeVariationValid(selectedVariations)) ||
    wc_product_type == "simple";

  /**
   * Check if the variation is a valid variation given the current selected options
   */
  function isValidVariation(current_variation, variation, for_slug = null) {
    if (isCustomConfiguration) {
      return true;
    }
    const variation_options = Object.entries(variation);

    for (const [slug, value] of variation_options) {
      //If the slug is the same as the slug we are checking, skip it because we want all the options for that slug
      if (for_slug && for_slug == slug) {
        continue;
      }
      if (value?.type == "any") {
        continue;
      }
      //Check if selectedVariation has a option selected that is not possible for this variation.
      if (
        current_variation[slug] &&
        getItemTermId(current_variation[slug]) !== getItemTermId(value)
      ) {
        return false;
      }
    }
    return true;
  }

  /**
   * Check if a given variation is valid
   */
  function isSomeVariationValid(_current_variation) {
    return (
      isCustomConfiguration || getValidVariationId(_current_variation) !== null
    );
  }

  /**
   * Get the valid variation id for the current selected options
   */
  function getValidVariationId(_current_variation) {
    if (isCustomConfiguration || !variations_list) {
      return null;
    }

    const [id] =
      Object.entries(variations_list).find(([, variation]) => {
        const is_valid = isValidVariation(_current_variation, variation);
        return is_valid;
      }) || [];

    return id || null;
  }

  /**
   * Checks if the current variation is valid, if not, it will remove the last selected variation until it is valid
   * Only run this when the isCustomConfiguration changes to false
   */
  useEffect(() => {
    if (
      !(
        isCustomConfiguration === false &&
        Object.keys(selectedVariations).length > 0
      )
    ) {
      return;
    }
    let current_variation = { ...selectedVariations };

    const is_valid_variation_selected = isSomeVariationValid(current_variation);

    if (!is_valid_variation_selected) {
      for (const slug of [...order].reverse()) {
        current_variation[slug] = null;

        const is_valid_variation_selected =
          isSomeVariationValid(current_variation);

        if (is_valid_variation_selected) {
          break;
        }
      }
    }
    setSelectedVariations(current_variation);
  }, [isCustomConfiguration]);

  function getItemTermId(item) {
    return item?.value?.term_id || item?.value;
  }

  function getVariationWithValidStatus(for_slug = null) {
    if (!variations_list) {
      return [];
    }
    return Object.values(variations_list).map((variation) => {
      //If the variation is custom, we want to show all options
      if (isCustomConfiguration) {
        return variation;
      }

      const is_valid = isValidVariation(
        selectedVariations,
        variation,
        for_slug
      );

      return { ...variation, disabled: !is_valid };
    });
  }

  function getValidVariations() {
    return getVariationWithValidStatus().filter(
      (item) => item?.disabled == false
    );
  }

  /**
   * Get all the possible variations options for a given slug
   */
  function getPossibleVariationOptions(for_slug) {
    //create a list of options from
    const variation_list = getVariationWithValidStatus(for_slug);

    //list of all enabled variations
    const enabled_variation_list = variation_list.filter(
      (item) => !item?.disabled
    );

    //list of all options for slug
    const all_options = variation_list.reduce((total, variation) => {
      const variation_object = variation[for_slug];

      const to_check_terms =
        variation_object?.type == "any"
          ? variation_object?.options
          : [variation_object];

      for (const term of to_check_terms) {
        const has_term = total.some(
          (item) => getItemTermId(item) == getItemTermId(term)
        );

        if (!has_term) {
          total.push(term);
        }
      }
      return total;
    }, []);

    //add disabled status to all options
    const all_options_with_status = all_options.map((option) => {
      const enabled = enabled_variation_list.some(
        (item) =>
          getItemTermId(item[for_slug]) == getItemTermId(option) ||
          item[for_slug]?.type == "any"
      );

      return { ...option, disabled: !enabled };
    });

    return all_options_with_status;
  }

  /**
   *
   */
  function handleFormSubmit(e) {
    if (!isSubmittable) {
      return;
    }
    e.preventDefault();

    const { ID, title, thumbnail, post_name } = product || {};
    const variation_id = getValidVariationId(selectedVariations);
    const form = e?.target;
    const formData = new FormData(form);
    const form_fields = Object.fromEntries(formData.entries());

    const product_id = addToCart({
      ...form_fields,
      product_id: ID,
      variation_id,
      is_custom: isCustomConfiguration,
      product: {
        title,
        thumbnail: selected_image || thumbnail,
        slug: post_name,
      },
      quantity,
      selected_options: selectedVariations,
    });

    const product_options = { ...extra_options, ...selectedVariations };

    // Get the first available preparation package
    const all_possible_preparation_package_for_product_with_attributes =
      preparation_package_for_product?.map((item) => {
        const { attributes } = item;

        const matches_each_attribute = Object.entries(attributes).every(
          ([cat, cat_list]) => {
            const includes_cat = cat_list.some(({ term_id }) => {
              return getItemTermId(product_options[cat]) == term_id;
            });

            return includes_cat;
          }
        );

        return matches_each_attribute ? item : [];
      });

    const preparation_package_for_product_with_attributes =
      all_possible_preparation_package_for_product_with_attributes && (
        all_possible_preparation_package_for_product_with_attributes.reduce(
          (best, item) => {
            if (!best) {
              return item;
            }
            if (item?.max_depth > best?.max_depth) {
              return item;
            }
            return best;
          },
          null
        )
      )

    const preparation_id =
      preparation_package_for_product_with_attributes?.id || null;

    setClearForm(Date.now());
    setSelectedVariations({});
    setOverlay(
      <ProductPreparePackage
        preparation_id={preparation_id}
        product_id={product_id}
        overlay_settings={overlay_settings}
        upsell={upsell}
        updateCartItemPreparation={updateCartItemPreparation}
      />,
      ModalCustomizationWithoutPaddingSmall
    );
  }

  /**
   * Helper function to check if a given value is within a range
   */
  function validateRange(value, ranges) {
    if (!ranges) {
      return true;
    }
    const _value = parseInt(value);
    if (isNaN(_value)) {
      return false;
    }

    const fits_within = ranges.some(
      ([min, max]) => _value >= min && _value <= max
    );

    return fits_within;
  }

  const available_product = !(
    product?.wc_product_type != "simple" && variations?.values?.length == 0
  );

  const variation_id = getValidVariationId(selectedVariations);
  const valid_variations = getValidVariations();

  useEffect(() => {
    if (!setSelectedVariationId) {
      return;
    }
    const id = valid_variations?.length == 1 ? variation_id : null;
    setSelectedVariationId(id);
  }, [valid_variations, variation_id]);

  const product_price = useMemo(() => {
    let price = null;
    if (!prices || !variation_price_ranges) {
      return toEuroFormat(price);
    }

    if (!is_variable_product) {
      price = prices[product.ID]?.price || null;
      return toEuroFormat(price);
    }

    if (Object.keys(selectedVariations || {}).length == 0) {
      const product_price_ranges = variation_price_ranges[product.ID];
      const { max_price, min_price } = product_price_ranges || {};

      if (max_price && min_price && max_price != min_price) {
        price = toEuroFormat(min_price) + " tot " + toEuroFormat(max_price);
      } else {
        price = toEuroFormat(min_price) || null;
      }

      return price;
    }

    const variation_id = getValidVariationId(selectedVariations) || null;
    if (!variation_id) {
      return price;
    }

    price = prices[variation_id]?.price ? toEuroFormat(prices[variation_id]?.price) : null;

    return price;
  }, [prices, variation_price_ranges, is_variable_product, selectedVariations]);

  return (
    <Block className="w-full p-0 bg-transparent md:p-0 lg:px-8 lg:py-8 lg:bg-light_grey">
      <h4>Offerte aanvragen</h4>
      {!available_product && (
        <div className="mb-2 text-red-500">
          Helaas, dit product is niet beschikbaar.
        </div>
      )}
      <form
        onSubmit={handleFormSubmit}
        key={clearForm}
      >
        <div className="flex flex-col gap-2">
          {order && order.length > 0 && (
            <div className="grid grid-cols-1 gap-2">
              {(order || []).map((slug) => {
                const values = getPossibleVariationOptions(slug);

                const is_hoogte =
                  product?.customize_fields?.hoogte_attribuut == slug;

                const is_breedte =
                  product?.customize_fields?.breedte_attribuut == slug;

                const isCustom = (is_hoogte || is_breedte) && is_customizable;
                const info = variation_info[slug];
                let input_label = "Selecteer " + translations[slug].toLowerCase();
                if (info?.acf_eigenschap_selectie_titel) {
                  input_label = info?.acf_eigenschap_selectie_titel;
                }

                const size_range = is_breedte
                  ? active_price_list?.width_ranges
                  : is_hoogte
                    ? active_price_list?.height_ranges
                    : null;

                const is_error = is_hoogte
                  ? hoogte_error
                  : is_breedte
                    ? breedte_error
                    : null;

                return (
                  <PropertyOption
                    is_error={is_error}
                    size_range={size_range}
                    key={slug}
                    selected={selectedVariations[slug]}
                    info={info}
                    isCustom={isCustom}
                    inputLabel={input_label}
                    label={translations[slug]}
                    field={slug}
                    options={values}
                    setSelected={(item) => {
                      if (item) {
                        delete item.disabled;
                      }
                      setSelectedVariations((selectedVariations) => ({
                        ...selectedVariations,
                        [slug]: item,
                      }));
                    }}
                  />
                );
              })}
            </div>
          )}

          <InputFieldGrinwis
            type="textarea"
            label="Heeft u nog opmerkingen?"
            name="remarks"
            rows={3}
          />

          <div>
            <div className="flex flex-row h-12 gap-2">
              <NumberInputField
                onChange={(value) => setQuantity(value)}
                value={quantity}
              />
              <ButtonGsfurn
                size="xl"
                className="justify-center flex-1"
                disabled={!isSubmittable}
                color="cart_add_btn"
                type="submit"
              >
                <div className="flex flex-row gap-2 md:gap-4">
                  <div>Voeg toe aan offerte</div>
                </div>
              </ButtonGsfurn>
            </div>

            {(showPrice && product_price) && (
              <div className="flex items-center gap-3 mt-4 md:mt-6">
                <span className="text-xl font-black md:text-2xl text-dark-text">{product_price}</span>
                <Badge
                  color="primary"
                  size="pill"
                  className="w-fit"
                >
                  Adviesprijs
                </Badge>
              </div>
            )}
          </div>
        </div>
      </form>
      {hoogte_error && <div className="mt-4 text-red-500">{hoogte_error}</div>}
      {breedte_error && (
        <div className="mt-4 text-red-500">{breedte_error}</div>
      )}

      <div className="flex flex-col justify-center w-full gap-2 mt-4 mb-4 leading-normal md:mb-0 md:mt-6">
        {settings?.options?.usps?.map((usp, idx) => {
          if (idx == 3) {
            return;
          }

          return (
            <div
              className="flex flex-row items-center gap-2"
              key={usp?.usp}
            >
              <FontAwesomeIcon
                icon={faCheck}
                className="w-auto text-xs text-primary"
              />
              <div className="text-xs text-placeholder">{usp?.usp}</div>
            </div>
          );
        })}
      </div>
    </Block>
  );
};

export default ProductProperties;
